import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Link, NavLink, useLocation } from 'react-router-dom';
import Home from './components/Home';
import Footer from './components/Footer';
import ThemeToggle from './components/ThemeToggle';
import Photo from './components/Photo';
import useAnalytics from './hooks/useAnalytics';
import photos from './asset/photos';
import ErrorBoundary from './components/ErrorBoundary';
import { whatsappLink } from './contactConfig';
import { NAV_PATHS } from './navigation';
import { LANGUAGES, localizePath, stripLang, useContent, useLang } from './i18n';
import seo from './i18n/seo.json';
import { useTheme } from './theme';
import logo from './asset/logo.webp';
import './styles/app.css';

// Other pages are split into their own chunks so the homepage loads less JavaScript.
const History = lazy(() => import('./components/History'));
const Products = lazy(() => import('./components/Products'));
const Uses = lazy(() => import('./components/Uses'));
const Contact = lazy(() => import('./components/Contact'));
const Ordering = lazy(() => import('./components/Ordering'));
const NotFound = lazy(() => import('./components/NotFound'));

// Page components by English path; each is also served under /es
const PAGES = {
  '/': Home,
  '/products': Products,
  '/uses': Uses,
  '/how-we-work': Ordering,
  '/true-cinnamon': History,
  '/contact': Contact,
};

// Photos for the hero slides; the text for each slide is in i18n/<lang>/common.js
const heroPhotos = [photos.withLeaves, photos.goldenHour, photos.nurserySeedlings];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
    <path d="M16.004 0h-.008C7.174 0 0 7.176 0 16c0 3.5 1.128 6.744 3.046 9.378L1.054 31.29l6.156-1.97A15.903 15.903 0 0016.004 32C24.826 32 32 24.822 32 16S24.826 0 16.004 0zm9.342 22.616c-.39 1.1-1.932 2.012-3.182 2.278-.856.18-1.974.324-5.738-1.234-4.818-1.992-7.92-6.894-8.16-7.214-.232-.32-1.95-2.6-1.95-4.96s1.234-3.52 1.672-4.002c.39-.43.924-.618 1.432-.618.174 0 .33.016.472.03.438.018.658.044 .948.732.36.858 1.24 3.018 1.348 3.238.11.22.184.478.036.758-.14.288-.21.466-.418.718-.208.252-.438.562-.624.754-.208.218-.424.454-.182.89.242.436 1.076 1.776 2.312 2.878 1.59 1.418 2.93 1.858 3.344 2.064.314.156.686.13.936-.144.318-.346.71-.92 1.11-1.486.284-.404.644-.454.99-.306.352.14 2.224 1.048 2.606 1.24.382.19.636.286.73.444.092.158.092.914-.298 2.014z" fill="currentColor"/>
  </svg>
);

const SUGGESTION_DISMISSED_KEY = 'sy-es-suggestion-dismissed';

// Offers the Spanish site to visitors whose browser prefers Spanish. A suggestion,
// not a redirect: redirects confuse search engines and annoy people who chose English.
function LanguageSuggestion({ lang, path, messages }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let dismissed = false;
    try { dismissed = localStorage.getItem(SUGGESTION_DISMISSED_KEY) === '1'; } catch (e) { /* storage blocked */ }
    const prefersSpanish = (navigator.languages || [navigator.language]).some((l) => l && l.toLowerCase().startsWith('es'));
    setVisible(lang === 'en' && prefersSpanish && !dismissed);
  }, [lang]);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(SUGGESTION_DISMISSED_KEY, '1'); } catch (e) { /* storage blocked */ }
    setVisible(false);
  };

  return (
    <div className="lang-suggestion" lang="es" role="region" aria-label={messages.text}>
      <span>{messages.text}</span>
      <Link to={localizePath(path, 'es')} className="lang-suggestion-action" onClick={dismiss}>{messages.action}</Link>
      <button type="button" className="lang-suggestion-close" onClick={dismiss} aria-label={messages.dismiss}>×</button>
    </div>
  );
}

function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const location = useLocation();
  const lang = useLang();
  const t = useContent('common');
  const path = stripLang(location.pathname); // the page's English path, e.g. "/products"
  const lp = (p) => localizePath(p, lang);
  const heroSlides = t.hero.slides.map((slide, i) => ({ ...slide, id: i, photo: heroPhotos[i] }));
  const otherLang = lang === 'en' ? 'es' : 'en';
  const { theme, toggle: toggleTheme } = useTheme();

  // Keep <html lang>, the title and the description in step with the page, before
  // analytics records the page view
  useEffect(() => {
    const pageSeo = seo[lang][path] || seo[lang]['404'];
    document.documentElement.lang = LANGUAGES[lang].htmlLang;
    document.title = pageSeo.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', pageSeo.description);
  }, [lang, path]);

  // Track page views with Google Analytics
  useAnalytics();
  
  // Check if we're on the homepage
  const isHomePage = path === '/';

  const nextSlide = useCallback(() => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % heroPhotos.length);
    setTimeout(() => setIsTransitioning(false), 150);
  }, [isTransitioning]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    if (!location.hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // Links like /products#hamburg: the page may still be loading, so retry briefly
    let frame;
    let tries = 0;
    const scrollToTarget = () => {
      const target = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (target) target.scrollIntoView();
      else if (tries++ < 60) frame = requestAnimationFrame(scrollToTarget);
    };
    scrollToTarget();
    return () => cancelAnimationFrame(frame);
  }, [location]);

  // Auto-advance carousel (only on the homepage). Restarts whenever the slide
  // changes, so picking a slide manually gives it a full turn on screen.
  useEffect(() => {
    if (!isHomePage || prefersReducedMotion()) return;
    const timer = setTimeout(nextSlide, 6000);
    return () => clearTimeout(timer);
  }, [nextSlide, isHomePage, currentSlide]);

  return (
    <div className="app">
      {/* Hero Carousel Section - Only on Homepage */}
      {isHomePage && (
        <header className="hero-carousel theme-dark">
          {heroSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              aria-hidden={index !== currentSlide}
            >
              <Photo
                photo={slide.photo}
                className="hero-slide-image"
                sizes="100vw"
                priority={index === 0}
                loading="eager"
                // The later slides aren't seen for 6s; don't let them compete with the first
                fetchpriority={index === 0 ? 'high' : 'low'}
              />
              <div className="hero-slide-overlay"></div>
            </div>
          ))}
          
          {/* Content - Left Bottom Aligned */}
          <div className="hero-content">
            {/* The slide titles rotate, so the page's main heading is a fixed, visually hidden one */}
            <h1 className="visually-hidden">{t.hero.heading}</h1>
            <div className={`hero-text ${isTransitioning ? 'transitioning' : ''}`}>
              <div className="hero-badge" key={`badge-${currentSlide}`}>
                {heroSlides[currentSlide].badge}
              </div>
              <p className="hero-title">
                <span className="hero-title-main" key={`title-${currentSlide}`}>
                  {heroSlides[currentSlide].title}
                </span>
              </p>
              <p className="hero-subtitle" key={`subtitle-${currentSlide}`}>
                {heroSlides[currentSlide].subtitle}
              </p>
            </div>

            {/* Outside .hero-text so the buttons don't fade with each slide change */}
            <div className="hero-actions">
              <Link to={lp('/products')} className="hero-btn hero-btn--primary">{t.hero.viewProducts}</Link>
              <Link to={lp('/contact')} className="hero-btn hero-btn--ghost">{t.hero.requestQuote}</Link>
            </div>
          </div>

          {/* Animated Transition Indicator - Right Bottom */}
          <div className="transition-indicator">
            <div className="pill-dots">
              {heroSlides.map((slide, index) => (
                <button
                  key={slide.id}
                  type="button"
                  className={`pill-dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => setCurrentSlide(index)}
                  aria-label={t.hero.slideLabel(index + 1, slide.title)}
                  aria-current={index === currentSlide}
                />
              ))}
            </div>
          </div>
        </header>
      )}

      {/* Navigation */}
      {/* Over the home photo the bar is transparent and always uses dark-theme colours */}
      <nav className={`navbar ${isScrolled || !isHomePage ? 'navbar-scrolled' : ''} ${isHomePage && !isScrolled && !isMobileMenuOpen ? 'theme-dark' : ''}`}>
        <div className="navbar-container">
          <NavLink to={lp('/')} className="navbar-logo" aria-label={t.homeLinkLabel} translate="no">
            <img src={logo} alt="Spice Yield" className="logo-image" />
            <span className="logo-text">Spice Yield</span>
          </NavLink>

          <ul className={`navbar-nav ${isMobileMenuOpen ? 'nav-open' : ''}`}>
            {NAV_PATHS.map((navPath) => (
              <li key={navPath}>
                {/* On desktop, Contact (where quotes are requested) is styled as a button */}
                <NavLink
                  to={lp(navPath)}
                  className={({ isActive }) => `nav-link${isActive ? ' active' : ''}${navPath === '/contact' ? ' nav-link--cta' : ''}`}
                >
                  {t.nav[navPath]}
                </NavLink>
              </li>
            ))}
            {/* Language and theme inside the mobile menu; desktop shows them in the bar */}
            <li className="nav-lang-item">
              <ThemeToggle theme={theme} toggle={toggleTheme} labels={t} className="theme-toggle" />
              <Link
                to={localizePath(path, otherLang)}
                className="nav-lang-link"
                hrefLang={otherLang}
                lang={otherLang}
                translate="no"
              >
                {LANGUAGES[otherLang].label}
              </Link>
            </li>
          </ul>

          <div className="navbar-actions">
            <ThemeToggle theme={theme} toggle={toggleTheme} labels={t} className="theme-toggle theme-toggle--bar" />
            {/* Language switch: same page in the other language */}
            <Link
              to={localizePath(path, otherLang)}
              className="lang-switch"
              hrefLang={otherLang}
              lang={otherLang}
              aria-label={`${t.languageSwitch}: ${LANGUAGES[otherLang].label}`}
              translate="no"
            >
              {LANGUAGES[otherLang].short}
            </Link>
            <a
              href={whatsappLink(t.whatsapp.defaultMessage)}
              className="navbar-whatsapp"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t.whatsapp.label}
              title={t.whatsapp.label}
            >
              <WhatsAppIcon className="navbar-whatsapp-icon" />
            </a>
            <button
              className={`mobile-menu-btn ${isMobileMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={t.menuToggle}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className={`main-content ${!isHomePage ? 'with-navbar-offset' : ''}`}>
        <ErrorBoundary key={location.pathname} messages={t.error}>
          <Suspense fallback={<div className="page-loading" aria-busy="true" />}>
            <Routes>
              {Object.keys(LANGUAGES).flatMap((routeLang) =>
                Object.entries(PAGES).map(([pagePath, Page]) => (
                  <Route key={`${routeLang}${pagePath}`} path={localizePath(pagePath, routeLang)} element={<Page />} />
                ))
              )}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </main>

      <Footer theme={theme} toggleTheme={toggleTheme} />

      <LanguageSuggestion lang={lang} path={path} messages={t.languageSuggestion} />
    </div>
  );
}

export default App;
